import { Check, ChevronsUpDown, Plus } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/** The portalled list sits outside a dialog's scroll lock, which would swallow wheel/touch scrolling. */
const stopScrollPropagation = (e: React.SyntheticEvent) => e.stopPropagation();
const KEEP_SCROLL_INSIDE = {
  onWheel: stopScrollPropagation,
  onTouchMove: stopScrollPropagation,
};

export interface ComboboxOption {
  value: string;
  label: React.ReactNode;
  labelString?: string;
  icon?: React.ReactNode;
  group?: string;
}

interface ComboboxProps {
  "aria-label"?: string;
  options: ComboboxOption[];
  value?: string;
  onValueChange?: (value: string) => void;
  defaultValue?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyState?: React.ReactNode;
  triggerClassName?: string;
  triggerTabIndex?: number;
  contentClassName?: string;
  listClassName?: string;
  /** Control the popover open state */
  open?: boolean;
  /** Callback when the popover open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Allow popover to be rendered in a portal or not. Defaults to true */
  portal?: boolean;
  /** Disable the combobox trigger */
  disabled?: boolean;
  /** Default filter value */
  defaultFilter?: string;
  /** When true, the popover will open when the component is mounted and the input will be focused */
  autoFocus?: boolean;
  /** When provided, renders a "Create" footer. Called with the current search string. */
  onCreateNew?: (search: string) => void;
  /**
   * When provided, renders a footer entry after the list (e.g. "Search all…").
   * The popover stays open; the caller decides what changes.
   */
  footerAction?: { label: string; icon?: React.ReactNode; onSelect: () => void };
}

const ComboboxComponent = React.forwardRef<HTMLButtonElement, ComboboxProps>(
  (
    {
      options,
      "aria-label": ariaLabel,
      value: controlledValue,
      onValueChange,
      defaultValue,
      placeholder = "Select an option...",
      searchPlaceholder = "Search...",
      emptyState = "No results found.",
      triggerClassName,
      triggerTabIndex,
      contentClassName,
      listClassName,
      open: controlledOpen,
      onOpenChange,
      portal = true,
      disabled,
      defaultFilter,
      autoFocus = false,
      onCreateNew,
      footerAction,
    },
    ref,
  ) => {
    const [uncontrolledValue, setUncontrolledValue] = React.useState<
      string | undefined
    >(defaultValue);
    const isControlled = controlledValue !== undefined;
    const currentValue = isControlled ? controlledValue : uncontrolledValue;

    const [isOpen, setIsOpen] = React.useState(autoFocus);
    const isPopoverControlled = controlledOpen !== undefined;
    const open = isPopoverControlled ? controlledOpen : isOpen;

    const [inputValue, setInputValue] = React.useState(defaultFilter);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const listRef = React.useRef<HTMLDivElement>(null);

    // Focus the input when the popover opens and autoFocus is true
    React.useEffect(() => {
      if (open && autoFocus && inputRef.current) {
        // Small delay to ensure the popover is fully rendered
        const timer = setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
        return () => clearTimeout(timer);
      }
    }, [open, autoFocus]);

    const handleOpenChange = (newState: boolean) => {
      setIsOpen(newState);
      onOpenChange?.(newState);

      if (newState) {
        setTimeout(() => {
          const listElement = listRef.current;
          if (listElement && currentValue) {
            // Escape quotes/backslashes for the attribute selector
            const selectorValue = currentValue
              .replace(/\\/g, "\\\\")
              .replace(/"/g, '\\"');
            const selectedItem = listElement.querySelector(
              `[cmdk-item][data-value="${selectorValue}"]`,
            ) as HTMLElement | null;
            selectedItem?.scrollIntoView({ block: "start" });
          }
        }, 50);
      }
    };

    const handleValueChange = (newValue: string) => {
      if (!isControlled) {
        setUncontrolledValue(newValue);
      }
      onValueChange?.(newValue);
      handleOpenChange(false);
    };

    const selectedOption = React.useMemo(
      () => options.find((option) => option.value === currentValue),
      [options, currentValue],
    );

    const hasGroups = options.some((option) => option.group);

    const groupedOptions = React.useMemo(() => {
      if (!hasGroups) return null;
      const groups = new Map<string, ComboboxOption[]>();
      for (const option of options) {
        const group = option.group ?? "Other";
        if (!groups.has(group)) {
          groups.set(group, []);
        }
        groups.get(group)!.push(option);
      }
      return groups;
    }, [options, hasGroups]);

    const getItemStringValue = (option: ComboboxOption): string => {
      if (option.labelString) {
        return option.labelString;
      }
      if (typeof option.label === "string") {
        return option.label;
      }
      return option.value;
    };

    return (
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            variant="outline"
            role="combobox"
            aria-label={ariaLabel}
            aria-expanded={open}
            className={cn("w-[200px] justify-between", triggerClassName)}
            disabled={disabled}
            tabIndex={triggerTabIndex}
          >
            <span className="flex items-center gap-2 overflow-hidden">
              {selectedOption?.icon}
              <span className="truncate">
                {selectedOption ? selectedOption.label : placeholder}
              </span>
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          portal={portal}
          {...KEEP_SCROLL_INSIDE}
          className={cn(
            "w-[--radix-popover-trigger-width] p-0 overflow-hidden",
            contentClassName,
          )}
          align="start"
          data-entry-editor
        >
          <Command className="overflow-hidden h-full flex flex-col">
            <CommandInput
              ref={inputRef}
              placeholder={searchPlaceholder}
              value={inputValue}
              onValueChange={setInputValue}
            />
            <CommandList
              ref={listRef}
              className={cn("overflow-y-auto", listClassName)}
              // The popover is portaled out of any open dialog, whose scroll
              // lock would otherwise swallow the wheel and leave the list stuck.
              onWheel={(e) => e.stopPropagation()}
              onTouchMove={(e) => e.stopPropagation()}
            >
              <CommandEmpty>{emptyState}</CommandEmpty>
              {groupedOptions
                ? Array.from(groupedOptions.entries()).map(
                    ([group, groupOptions]) => (
                      <CommandGroup key={group} heading={group}>
                        {groupOptions.map((option) => (
                          <CommandItem
                            key={option.value}
                            value={option.value}
                            keywords={[getItemStringValue(option)]}
                            onSelect={() => {
                              handleValueChange(option.value);
                            }}
                            className="gap-2"
                          >
                            <Check
                              className={cn(
                                "size-4",
                                currentValue === option.value
                                  ? "opacity-100"
                                  : "opacity-0",
                              )}
                              aria-hidden="true"
                            />
                            {option.icon && (
                              <span className="shrink-0">{option.icon}</span>
                            )}
                            <span className="flex-1 truncate">
                              {option.label}
                            </span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    ),
                  )
                : <CommandGroup>
                    {options.map((option) => (
                      <CommandItem
                        key={option.value}
                        value={option.value}
                        keywords={[getItemStringValue(option)]}
                        onSelect={() => {
                          handleValueChange(option.value);
                        }}
                        className="gap-2"
                      >
                        <Check
                          className={cn(
                            "size-4",
                            currentValue === option.value
                              ? "opacity-100"
                              : "opacity-0",
                          )}
                          aria-hidden="true"
                        />
                        {option.icon && (
                          <span className="shrink-0">{option.icon}</span>
                        )}
                        <span className="flex-1 truncate">{option.label}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
              }
            </CommandList>
            {onCreateNew && (
              <div className="border-t">
                <Button
                  variant="ghost"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onCreateNew(inputValue?.trim() ?? "");
                    handleOpenChange(false);
                  }}
                  className="flex w-full items-center gap-1.5 px-3 py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                >
                  <Plus className="h-3.5 w-3.5 shrink-0" />
                  {inputValue?.trim() ? `Create "${inputValue.trim()}"` : "Create new"}
                </Button>
              </div>
            )}
            {footerAction && (
              <div className="border-t">
                <Button
                  variant="ghost"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    footerAction.onSelect();
                  }}
                  className="flex w-full items-center gap-1.5 px-3 py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                >
                  {footerAction.icon}
                  {footerAction.label}
                </Button>
              </div>
            )}
          </Command>
        </PopoverContent>
      </Popover>
    );
  },
);

ComboboxComponent.displayName = "Combobox";

export const Combobox = ComboboxComponent;

// ── Multi-select Combobox ────────────────────────────────────────────────────

interface MultiComboboxProps {
  options: ComboboxOption[];
  values: string[];
  onValuesChange: (values: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyState?: React.ReactNode;
  triggerClassName?: string;
  portal?: boolean;
  disabled?: boolean;
}

export function MultiCombobox({
  options,
  values,
  onValuesChange,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyState = "No results found.",
  triggerClassName,
  portal = true,
  disabled,
}: MultiComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const selected = new Set(values);

  const toggle = (value: string) => {
    const next = new Set(selected);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onValuesChange(Array.from(next));
  };

  const selectedLabels = options
    .filter((o) => selected.has(o.value))
    .map((o) => (typeof o.label === "string" ? o.label : o.value));

  const triggerLabel =
    selectedLabels.length === 0
      ? placeholder
      : selectedLabels.length <= 2
        ? selectedLabels.join(", ")
        : `${selectedLabels.slice(0, 2).join(", ")} +${selectedLabels.length - 2}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-[200px] justify-between", triggerClassName)}
          disabled={disabled}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        portal={portal}
        {...KEEP_SCROLL_INSIDE}
        className="w-[--radix-popover-trigger-width] p-0 overflow-hidden"
        align="start"
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList className="overflow-y-auto">
            <CommandEmpty>{emptyState}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  keywords={[
                    typeof option.label === "string"
                      ? option.label
                      : option.labelString ?? option.value,
                  ]}
                  onSelect={() => toggle(option.value)}
                  className="gap-2"
                >
                  <Check
                    className={cn(
                      "size-4",
                      selected.has(option.value) ? "opacity-100" : "opacity-0",
                    )}
                    aria-hidden="true"
                  />
                  <span className="flex-1 truncate">{option.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
